import React from 'react';
import { Badge } from '@components/ui/badge';
import { cn } from '@core/utils/cn';
import { ActivityPointsMultiplierTierGroupDto } from '../../core/api/FitnessActivityClient';
import {
    buildTierTimelineSegments,
    formatTierUnitLabel,
    resolveTierCountUnit
} from './tier-preview.utils';

const MIN_SEGMENT_WIDTH = 88;

const primaryMix = (percent: number) =>
    `color-mix(in oklab, var(--primary) ${Math.round(percent)}%, transparent)`;

export const TierPreview = ({
    row
}: {
    row: ActivityPointsMultiplierTierGroupDto;
}) => {
    const tierCountUnit = resolveTierCountUnit(
        row.activityType,
        row.workoutCategory
    );
    const tierCountUnitLabel = formatTierUnitLabel(tierCountUnit);
    const segments = buildTierTimelineSegments(row.tiers || []);

    if (segments.length === 0) {
        return (
            <div className="px-6 py-4">
                <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm font-medium">Tier Ranges</p>
                    <span className="text-xs text-muted-foreground">
                        {tierCountUnitLabel}
                    </span>
                </div>
                <p className="text-sm text-muted-foreground">
                    No tiers configured
                </p>
            </div>
        );
    }

    const multipliers = segments.map((segment) => segment.multiplier);
    const minMultiplier = Math.min(...multipliers);
    const maxMultiplier = Math.max(...multipliers);

    const getSegmentColorIntensity = (multiplier: number): number => {
        if (maxMultiplier === minMultiplier) {
            return 0.65;
        }
        return (multiplier - minMultiplier) / (maxMultiplier - minMultiplier);
    };

    return (
        <div className="px-6 py-4">
            <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">Tier Ranges</p>
                    <Badge variant="outline">{segments.length} tiers</Badge>
                </div>
                <span className="text-xs text-muted-foreground">
                    {tierCountUnitLabel}
                </span>
            </div>

            <div className="overflow-x-auto pb-1">
                <div className="flex min-w-full gap-1.5">
                    {segments.map((segment) => {
                        const intensity = getSegmentColorIntensity(
                            segment.multiplier
                        );
                        const backgroundAlpha = 11 + intensity * 24;
                        const borderAlpha = 25 + intensity * 35;

                        return (
                            <div
                                key={segment.key}
                                className={cn(
                                    'relative flex min-h-[58px] flex-col justify-between gap-1 overflow-hidden rounded-md border p-2',
                                    segment.isOpenEnded
                                        ? 'border-dashed'
                                        : 'border-solid'
                                )}
                                style={{
                                    width: `${segment.widthPercent}%`,
                                    minWidth: `${MIN_SEGMENT_WIDTH}px`,
                                    borderColor: primaryMix(borderAlpha),
                                    backgroundColor: primaryMix(backgroundAlpha)
                                }}
                            >
                                {segment.isOpenEnded && (
                                    <span className="absolute top-1 right-1.5 text-[0.65rem] font-bold text-primary">
                                        ↗
                                    </span>
                                )}
                                <span className="truncate text-xs font-semibold">
                                    {segment.rangeLabel}
                                </span>
                                <span className="self-end text-xs font-bold whitespace-nowrap">
                                    {segment.multiplierLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
